import React, { useEffect, useMemo } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Award,
  Boxes,
  Flame,
  LineChart,
  PieChart,
  Scale,
  TableProperties,
  TrendingUp,
  ArrowLeftRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

/**
 * Consolidated reports navigation.
 *
 * The 7 individual sidebar entries (ranking, comparativo, tendencia,
 * distribucion, heatmap, tabla, 3d) are merged into a single page with a
 * sticky horizontal tab bar. Each tab routes to its existing URL so
 * bookmarks, share links and tests keep working unchanged.
 *
 * Implementation notes:
 * - The tab bar is rendered above an `<Outlet />`. Children remain the
 *   untouched report components, mounted by the parent <Route>'s nested
 *   <Route>s.
 * - The currently-active tab is derived from the URL (not local state) so
 *   refreshing on `/reports/comparativo` highlights "Comparativo".
 * - Only tabs the current user is allowed to see are rendered.
 * - The most recently visited tab is persisted to `localStorage` so when a
 *   user clicks the sidebar entry ("/reports" with no sub-route) they land
 *   on the same view they were on before.
 */

const TABS = [
  { key: "ranking", path: "/reports/ranking", perm: "report:ranking", Icon: Award },
  { key: "comparativo", path: "/reports/comparativo", perm: "report:comparativo", Icon: Scale },
  { key: "tendencia", path: "/reports/tendencia", perm: "report:tendencia", Icon: TrendingUp },
  { key: "distribucion", path: "/reports/distribucion", perm: "report:distribucion", Icon: PieChart },
  { key: "type-comparison", path: "/reports/type-comparison", perm: "report:comparativo", Icon: ArrowLeftRight, labelKey: "typeComparison.tabLabel" },
  { key: "heatmap", path: "/reports/heatmap", perm: "report:heatmap", Icon: Flame },
  { key: "tabla", path: "/reports/tabla", perm: "report:tabla", Icon: TableProperties },
  { key: "3d", path: "/reports/3d", perm: "report:3d", Icon: Boxes },
];

const LS_KEY = "reports_active_tab";

export default function ReportsHub() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const location = useLocation();

  const allowed = useMemo(
    () => TABS.filter((tab) => hasPermission(tab.perm)),
    [hasPermission]
  );

  // Active tab is derived from the URL: split off the last segment.
  const currentKey = useMemo(() => {
    const seg = location.pathname.replace(/^\/reports\/?/, "").split("/")[0];
    return seg || null;
  }, [location.pathname]);

  // Persist the last-visited sub-route so we can restore on `/reports`.
  useEffect(() => {
    if (currentKey && TABS.some((tt) => tt.key === currentKey)) {
      try {
        window.localStorage.setItem(LS_KEY, currentKey);
      } catch {
        /* private mode / quota — silent */
      }
    }
  }, [currentKey]);

  // Bare /reports → pick the last-visited tab the user still has access to,
  // or fall back to the first allowed tab.
  if (!currentKey) {
    let target = null;
    try {
      const cached = window.localStorage.getItem(LS_KEY);
      if (cached && allowed.some((a) => a.key === cached)) {
        target = allowed.find((a) => a.key === cached);
      }
    } catch {
      /* ignore */
    }
    if (!target) target = allowed[0];
    if (!target) {
      return (
        <div className="flex h-[60vh] flex-col items-center justify-center gap-2 text-center">
          <h2 className="text-2xl font-semibold">403</h2>
          <p className="text-sm text-muted-foreground">
            {t("nav.noReportPermission", {
              defaultValue: "No tienes permiso para acceder a ningún reporte.",
            })}
          </p>
        </div>
      );
    }
    return <Navigate to={target.path} replace />;
  }

  // If the user doesn't have access to the active tab (e.g. revoked while
  // they were sitting on it) we'd still get a 403 from <ProtectedRoute>;
  // ReportsHub doesn't try to gate child routes itself.

  return (
    <div className="space-y-3">
      {/* Sticky horizontal tab bar */}
      <div
        className="sticky top-0 z-10 -mx-4 border-b border-border bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        data-testid="reports-hub-tabs"
      >
        <h1 className="pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("nav.reports")}
        </h1>
        <nav
          className="-mb-px flex gap-1 overflow-x-auto pt-2"
          role="tablist"
          aria-label={t("nav.reports")}
        >
          {allowed.map(({ key, path, Icon, labelKey }) => (
            <NavLink
              key={key}
              to={path}
              role="tab"
              aria-selected={currentKey === key}
              data-testid={`reports-tab-${key}`}
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-xs font-medium transition-colors",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                )
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {t(labelKey || `nav.${key === "3d" ? "vista3d" : key}`)}
              {key === "type-comparison" && (
                <span
                  className="ml-1 rounded-sm bg-primary/15 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-primary"
                  title="Esta vista se movió al módulo de Comparativos"
                  data-testid="type-comparison-moved-badge"
                >
                  movido →
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Active report renders here */}
      <Outlet />
    </div>
  );
}

// Exported so the sidebar test (and future tooling) can import the icon
// used for the consolidated nav entry without recomputing.
export const REPORTS_HUB_ICON = LineChart;
